import type { PendingSavedSearchPayload, SavedReportColumn, SavedReportItem } from '../api/types';

export const PENDING_SAVED_SEARCH_STORAGE_KEY = 'pendingSavedSearch';
export const PENDING_SAVED_SEARCH_EVENT = 'daater:pending-saved-search';

const toColumnFieldId = (column: SavedReportColumn) => {
  if (!column || typeof column !== 'object') return null;

  const field = column.field;
  if (typeof field === 'string' && field.trim()) {
    return field.trim();
  }

  if (field && typeof field === 'object' && typeof field._id === 'string' && field._id.trim()) {
    return field._id.trim();
  }

  return null;
};

export const buildSavedSearchFieldsPayload = (
  filters: SavedReportItem['filters']
): Record<string, unknown> => {
  if (!Array.isArray(filters)) return {};

  return filters.reduce<Record<string, unknown>>((acc, filter) => {
    if (!filter || typeof filter.field !== 'string' || !filter.field.trim()) {
      return acc;
    }

    if (filter.value === undefined || filter.value === null) {
      return acc;
    }

    acc[filter.field] = filter.value;
    return acc;
  }, {});
};

export const buildPendingSavedSearchPayload = (
  report: SavedReportItem,
  fallbackDataset?: string
): PendingSavedSearchPayload | null => {
  const dataset =
    typeof report.dataset === 'string' && report.dataset.trim()
      ? report.dataset.trim()
      : fallbackDataset?.trim();
  const table = typeof report.table === 'string' ? report.table.trim() : '';

  if (!dataset || !table || !report._id) {
    return null;
  }

  return {
    reportId: report._id,
    reportName: typeof report.name === 'string' ? report.name : 'Búsqueda guardada',
    dataset,
    table,
    fields: buildSavedSearchFieldsPayload(report.filters),
    columnFieldIds: Array.isArray(report.columns)
      ? report.columns.map(toColumnFieldId).filter((value): value is string => Boolean(value))
      : [],
    analysis: Array.isArray(report.analysis) ? report.analysis : [],
    detailed: Boolean(report.detailed),
    demoMode: Boolean(report.demoMode),
  };
};

export const loadPendingSavedSearchPayload = (): PendingSavedSearchPayload | null => {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(PENDING_SAVED_SEARCH_STORAGE_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as PendingSavedSearchPayload;
  } catch {
    window.localStorage.removeItem(PENDING_SAVED_SEARCH_STORAGE_KEY);
    return null;
  }
};

export const clearPendingSavedSearchPayload = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(PENDING_SAVED_SEARCH_STORAGE_KEY);
};

export const persistPendingSavedSearchPayload = (payload: PendingSavedSearchPayload) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(PENDING_SAVED_SEARCH_STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent(PENDING_SAVED_SEARCH_EVENT));
};
